/* Pages/NoticesPage.tsx */
import React, { useCallback, useState, useMemo, useEffect } from 'react';
import { Button, Typography, Select, message, Spin } from 'antd';
import dayjs from 'dayjs';

import { useAppDispatch, useAppSelector } from '../../Modules/Presentation/hooks';

import NoticeTable from './components/NoticeTable';
import NoticeWriteModal from './components/NoticeWriteModal';
import NoticeEditModal from './components/NoticeEditModal';
import NoticeViewModal from './components/NoticeViewModal';

import { selectUser } from '../../Modules/User/Presentation/selectors';
import { selectNotices } from '../../Modules/Notice/Presentation/selectors';
import { fetchNotices, removeNotice } from '../../Modules/Notice/Presentation/actions';
import { Notice } from '../../Modules/Notice/Model/Notice';
import { canManageNotices } from '../../Utils/limitedPartnerAdmin';

const { Title } = Typography;
const { Option } = Select;

const NoticesPage: React.FC = () => {
  /* ───────── store ───────── */
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(selectUser);
  const noticesRedux = useAppSelector(selectNotices);

  /* ───────── fetch once ───────── */
  useEffect(() => {
    if (noticesRedux.length === 0) {
      dispatch(fetchNotices()).unwrap().catch(() =>
        message.error('공지사항 정보를 불러오는데 실패하였습니다.'),
      );
    }
  }, [dispatch, noticesRedux.length]);

  /* ───────── local state ───────── */
  const [isWriteOpen, setIsWriteOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selected, setSelected] = useState<Notice | null>(null);

  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);

  /* ───────── util ───────── */
  const canWriteNotices = canManageNotices(currentUser);

  /** updated_at ▸ created_at 기준 내림차순 */
  const sortedNotices = useMemo(() => {
    return [...noticesRedux].sort((a, b) => {
      const aTime = dayjs(a.updated_at ?? a.created_at).valueOf();
      const bTime = dayjs(b.updated_at ?? b.created_at).valueOf();
      return bTime - aTime;   // 최신순
    });
  }, [noticesRedux]);

  /** 현재 페이지 slice */
  const pagedList = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedNotices.slice(start, start + perPage);
  }, [sortedNotices, page, perPage]);

  /* ───────── callbacks ───────── */
  const handleEdit = useCallback((n: Notice) => {
    setSelected(n);   // n은 테이블 Row 의 원본 Notice
    setEditOpen(true);
  }, []);

  const handleDelete = useCallback((n: Notice) => {
    dispatch(removeNotice(n.noticeId))
      .unwrap()
      .then(() => message.success('삭제되었습니다.'))
      .catch(() => message.error('삭제 실패'));
  }, [dispatch]);

  const handleView = useCallback((n: Notice) => {
    setSelected(n);
    setViewOpen(true);
  }, []);

  /* ───────── render ───────── */
  return (
    <div>
      <Title level={3}>공지사항 리스트</Title>

      {!currentUser ? (
        <Spin style={{ display: 'block', margin: '120px auto' }} />
      ) : (
        <>
          {/* 상단 툴바 */}
          <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
            <Select
              value={perPage}
              style={{ width: 120 }}
              onChange={v => { setPerPage(v); setPage(1); }}
            >
              {[10, 30, 50, 100].map(n => <Option key={n} value={n}>{n}개씩</Option>)}
            </Select>

            {canWriteNotices && (
              <Button type="primary" onClick={() => setIsWriteOpen(true)}>
                작성하기
              </Button>
            )}
          </div>

          {/* 테이블 */}
          <NoticeTable
            data={pagedList}
            total={sortedNotices.length}
            perPage={perPage}
            currentPage={page}
            onPageChange={setPage}
            onEdit={canWriteNotices ? handleEdit : undefined}
            onDelete={canWriteNotices ? handleDelete : undefined}
            onView={handleView}
            showActions={canWriteNotices}
          />

          {/* 모달들 */}
          <NoticeWriteModal
            open={isWriteOpen}
            onClose={() => setIsWriteOpen(false)}
          />

          <NoticeEditModal
            open={editOpen}
            notice={selected}
            onClose={() => { setEditOpen(false); setSelected(null); }}
          />

          <NoticeViewModal
            open={viewOpen}
            notice={selected}
            onClose={() => { setViewOpen(false); setSelected(null); }}
          />
        </>
      )}
    </div>
  );
};

export default NoticesPage;
